import './notifications.css';

import { RefresherEventDetail } from '@ionic/core';
import {
  IonChip,
  IonIcon,
  IonLabel,
  IonList,
  IonRefresher,
  IonRefresherContent,
  useIonViewWillEnter,
} from '@ionic/react';
import { mailOpenOutline } from 'ionicons/icons';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  EventChannelType,
  EventNotification,
  ListEventNotificationsDocument,
  ListEventNotificationsQuery,
  ListEventNotificationsQueryVariables,
  useListEventNotificationsQuery,
  useMarkEventNotificationsReadMutation,
} from '../../api/graphql/generated';
import {
  useOnNotification,
  useUnreadNotifications,
} from '../../api/hooks/event-notifications.hooks';
import { useListQueryWithInfiniteScroll } from '../../api/hooks/infinite-scroll.hooks';
import { logger } from '../../util/logger';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { IonItem } from '../ionic/IonItem';
import { NotificationItem } from './notification';
import { NotificationsGettingStarted } from './notifications-getting-started';

const NotificationList = (): ReactElement => {
  const { i18n } = useTranslation();
  const isAlreadyMounted = useRef<boolean>(false);

  const {
    infiniteScroll,
    result: { data, refetch, loading, error },
  } = useListQueryWithInfiniteScroll({
    useQuery: useListEventNotificationsQuery,
    input: {
      channelType: EventChannelType.USER,
    },
    queryOptions: {
      fetchPolicy: 'cache-and-network',
    },
  });

  const unreadNotifications = useUnreadNotifications();

  useOnNotification((unread) => {
    if (unread !== undefined) {
      logger.info('refetching notifications');
      refetch();
    }
  });

  useIonViewWillEnter(() => {
    if (isAlreadyMounted.current === true) {
      refetch();
    }
    isAlreadyMounted.current = true;
  });

  const [markEventNotificationsReadMutation] =
    useMarkEventNotificationsReadMutation();

  const markAllRead = useCallback(() => {
    markEventNotificationsReadMutation({
      variables: {
        input: {},
      },
      update: (cache) => {
        const variables: ListEventNotificationsQueryVariables = {
          input: { channelType: EventChannelType.USER },
        };
        const cacheData = cache.readQuery<ListEventNotificationsQuery>({
          query: ListEventNotificationsDocument,
          variables,
        });
        const items: EventNotification[] = [
          ...(cacheData?.listEventNotifications.items ?? []),
        ] as EventNotification[];
        const updatedItems = items.map((i) => ({ ...i, read: true }));
        cache.writeQuery({
          query: ListEventNotificationsDocument,
          variables,
          data: {
            listEventNotifications: {
              ...cacheData?.listEventNotifications,
              items: updatedItems,
            },
          },
        });
      },
    });
  }, [markEventNotificationsReadMutation]);

  const onMarkAllRead = (
    e: React.MouseEvent<HTMLIonChipElement, MouseEvent>
  ) => {
    e.preventDefault();
    markAllRead();
  };

  const doRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    event.detail.complete();
  };

  const notifications = useMemo(() => {
    return (data?.listEventNotifications.items ?? []) as EventNotification[];
  }, [data?.listEventNotifications?.items]);

  useEffect(() => {
    // auto mark all notifications as read if we have a notification
    // count > 0 and we don't actually have any notifications in the
    // list. this could happen because we purge notifications after
    // a certain time period on the server

    if (
      !loading &&
      notifications.length === 0 &&
      unreadNotifications !== undefined &&
      unreadNotifications > 0
    ) {
      markAllRead();
    }
  }, [loading, notifications, unreadNotifications, markAllRead]);

  return (
    <>
      <IonItem lines="full">
        <IonChip
          slot="end"
          color="primary"
          disabled={false} // could disable button if no unread
          onClick={onMarkAllRead}
        >
          <IonIcon icon={mailOpenOutline}></IonIcon>
          <IonLabel>{i18n.t('common:notifications_mark_all_read')}</IonLabel>
        </IonChip>
      </IonItem>
      <LoadingRetry
        loading={loading}
        refetch={refetch}
        error={error}
        results={{
          hasResults: notifications.length > 0,
          noResultsComponent: <NotificationsGettingStarted />,
        }}
      >
        <div className="safe-area-bottom">
          <IonList className="safe-area-bottom">
            {notifications.map((notification) => {
              return (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                />
              );
            })}
          </IonList>
          {infiniteScroll}
        </div>
      </LoadingRetry>
      <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
        <IonRefresherContent></IonRefresherContent>
      </IonRefresher>
    </>
  );
};

export { NotificationList };
